/**
 * BenchmarkCard — Sprint 56: Crop & Farm Benchmark Comparison
 *
 * Displays quartile bar chart, percentile rank, improvement hints,
 * and KTBL source badge for a single crop's contribution margin
 * compared against regional/national benchmarks.
 *
 * Also exports FarmBenchmarkSummary for the dashboard-level view.
 */

import { useState, useEffect } from 'react';
import { apiClient } from '../lib/apiClient';
import { useTranslation } from 'react-i18next';

// ── Formatters ────────────────────────────────────────────────────

const EUR = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

// ── Helpers ───────────────────────────────────────────────────────

function percentileCategory(pct) {
  if (pct >= 75) return 'top_quartile';
  if (pct >= 50) return 'above_average';
  if (pct >= 25) return 'below_average';
  return 'bottom_quartile';
}

const CATEGORY_CONFIG = {
  top_quartile:    { emoji: '\u{1F3C6}', key: 'benchmark.topQuartile',    color: 'text-green-400' },
  above_average:   { emoji: '\u2705',    key: 'benchmark.aboveAverage',   color: 'text-green-400' },
  below_average:   { emoji: '\u26A0\uFE0F', key: 'benchmark.belowAverage',  color: 'text-orange-400' },
  bottom_quartile: { emoji: '\u{1F534}', key: 'benchmark.bottomQuartile', color: 'text-red-400' },
};

// ── Sub-components ────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="w-5 h-5 rounded-full border-2 border-[var(--aif-color-primary)] border-t-transparent animate-spin" />
    </div>
  );
}

/**
 * QuartileBar — horizontal range bar from bottom_25 to top_25
 * with the farmer's value marked as a diamond indicator.
 */
function QuartileBar({ bottom25, median, top25, farmerValue, label }) {
  const { t } = useTranslation();

  // Compute the full range with some padding
  const rangeMin = Math.min(bottom25, farmerValue ?? bottom25);
  const rangeMax = Math.max(top25, farmerValue ?? top25);
  const padding = (rangeMax - rangeMin) * 0.1 || 50;
  const min = rangeMin - padding;
  const max = rangeMax + padding;
  const span = max - min || 1;

  const pct = (v) => ((v - min) / span) * 100;

  const leftPct = pct(bottom25);
  const medPct = pct(median);
  const rightPct = pct(top25);
  const farmerPct = farmerValue != null ? pct(farmerValue) : null;

  return (
    <div className="space-y-2">
      {label && (
        <div className="text-xs font-semibold text-[var(--aif-text-primary)]">{label}</div>
      )}
      {/* Bar */}
      <div className="relative h-6 w-full rounded-full bg-[var(--aif-bg-elevated)] overflow-visible">
        {/* Red zone: bottom_25 → median */}
        <div
          className="absolute top-0 h-full rounded-l-full"
          style={{
            left: `${leftPct}%`,
            width: `${medPct - leftPct}%`,
            background: 'linear-gradient(90deg, rgba(239,68,68,0.5), rgba(251,146,60,0.5))',
          }}
        />
        {/* Green zone: median → top_25 */}
        <div
          className="absolute top-0 h-full rounded-r-full"
          style={{
            left: `${medPct}%`,
            width: `${rightPct - medPct}%`,
            background: 'linear-gradient(90deg, rgba(251,191,36,0.4), rgba(34,197,94,0.5))',
          }}
        />
        {/* Median line */}
        <div
          className="absolute top-0 h-full w-px bg-[var(--aif-text-muted)]"
          style={{ left: `${medPct}%` }}
        />
        {/* Farmer marker (diamond) */}
        {farmerPct != null && (
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10"
            style={{ left: `${Math.max(2, Math.min(98, farmerPct))}%` }}
          >
            <div className="w-4 h-4 bg-[var(--aif-color-primary)] rotate-45 rounded-sm shadow-lg border border-white/20" />
          </div>
        )}
      </div>

      {/* Labels below */}
      <div className="relative h-8 text-[10px] text-[var(--aif-text-muted)]">
        <div className="absolute" style={{ left: `${leftPct}%`, transform: 'translateX(-50%)' }}>
          <div className="text-center">
            <div>{Math.round(bottom25)} €</div>
            <div className="text-[9px]">{t('benchmark.bottom25') || 'Bottom 25%'}</div>
          </div>
        </div>
        <div className="absolute" style={{ left: `${medPct}%`, transform: 'translateX(-50%)' }}>
          <div className="text-center">
            <div>{Math.round(median)} €</div>
            <div className="text-[9px]">{t('benchmark.median') || 'Median'}</div>
          </div>
        </div>
        <div className="absolute" style={{ left: `${rightPct}%`, transform: 'translateX(-50%)' }}>
          <div className="text-center">
            <div>{Math.round(top25)} €</div>
            <div className="text-[9px]">{t('benchmark.top25') || 'Top 25%'}</div>
          </div>
        </div>
      </div>

      {/* Farmer value callout */}
      {farmerValue != null && (
        <div className="text-center text-xs font-bold text-[var(--aif-color-primary)] mt-1">
          {t('benchmark.yourValue')}: {Math.round(farmerValue)} €/ha
        </div>
      )}
    </div>
  );
}

/**
 * PercentileDisplay — large percentile number + category label
 */
function PercentileDisplay({ percentile }) {
  const { t } = useTranslation();
  const cat = percentileCategory(percentile);
  const cfg = CATEGORY_CONFIG[cat];

  return (
    <div className="text-center space-y-1">
      <div className="text-3xl font-bold text-[var(--aif-text-primary)]">
        {Math.round(percentile)}. {t('benchmark.percentile')}
      </div>
      <div className={`text-sm font-semibold ${cfg.color}`}>
        {cfg.emoji} {t(cfg.key)}
      </div>
    </div>
  );
}

/**
 * ImprovementHints — cards showing potential gains (only if percentile < 75)
 * Supports both string hints and object hints (with label/delta_db fields).
 */
function ImprovementHints({ hints, deltaDb }) {
  const { t } = useTranslation();

  if ((!hints || hints.length === 0) && !deltaDb) return null;

  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold text-[var(--aif-text-primary)]">
        {t('benchmark.improvementTitle')}
      </div>
      {deltaDb > 0 && (
        <div className="text-xs font-bold text-green-400 text-center">
          +{Math.round(deltaDb)} €/ha {t('benchmark.potential') || 'potential'}
        </div>
      )}
      <div className="space-y-1.5">
        {(hints || []).map((hint, i) => {
          const label = typeof hint === 'string' ? hint : hint.label;
          const delta = typeof hint === 'string' ? null : (hint.delta_db ?? hint.delta);
          return (
            <div
              key={i}
              className="flex items-center justify-between px-3 py-2 rounded-lg border border-green-500/20 bg-green-500/5"
            >
              <span className="text-xs text-[var(--aif-text-secondary)]">{label}</span>
              {delta != null && (
                <span className="text-xs font-bold text-green-400">
                  +{Math.round(delta)} €/ha
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Component: BenchmarkCard ─────────────────────────────────

export default function BenchmarkCard({ token, cropCode, cropName }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token || !cropCode) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await fetch(`/api/v1/aif/finance/benchmark/${cropCode}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error('not_found');
        const json = await r.json();
        if (!cancelled) setData(json);
      } catch {
        if (!cancelled) setError(t('benchmark.noData'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [token, cropCode, t]);

  if (loading) return <Spinner />;

  if (error || !data) {
    return (
      <div className="text-xs text-[var(--aif-text-muted)] p-4 text-center">
        {error || t('benchmark.noData')}
      </div>
    );
  }

  // Extract data from new API response structure
  const apiCropName = data.crop_name || cropName;
  const yourValues = data.your_values || {};
  const benchmark = data.benchmark || {};
  const yourPosition = data.your_position || {};
  const improvementPotential = yourPosition.improvement_potential || {};

  const percentile = yourPosition.percentile;
  const bottom25 = benchmark.q25;
  const median = benchmark.median;
  const top25 = benchmark.q75;
  const farmerValue = yourValues.db_i_per_ha;
  const hints = improvementPotential.hints || [];
  const deltaDb = improvementPotential.delta_db;
  const { source } = data;

  const showHints = percentile != null && percentile < 75 && (hints.length > 0 || deltaDb > 0);

  return (
    <div className="rounded-2xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4 space-y-5">
      {/* Title */}
      <div className="text-center">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--aif-text-muted)]">
          {t('benchmark.title')}
        </div>
        {apiCropName && (
          <div className="text-sm font-bold text-[var(--aif-text-primary)] mt-1">{apiCropName}</div>
        )}
      </div>

      {/* A) Quartile Bar Chart */}
      <QuartileBar
        bottom25={bottom25}
        median={median}
        top25={top25}
        farmerValue={farmerValue}
      />

      {/* B) Percentile Display */}
      <PercentileDisplay percentile={percentile} />

      {/* C) Improvement Hints */}
      {showHints && <ImprovementHints hints={hints} deltaDb={deltaDb} />}

      {/* D) Source badge */}
      {source && (
        <div className="text-[10px] text-[var(--aif-text-muted)] text-center pt-1 border-t border-[var(--aif-border-default)]">
          {t('benchmark.source')}: {source}
        </div>
      )}
    </div>
  );
}

// ── Farm-level Benchmark Summary ──────────────────────────────────

export function FarmBenchmarkSummary({ token }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await fetch('/api/v1/aif/finance/benchmark/farm', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error('not_found');
        const json = await r.json();
        if (!cancelled) setData(json);
      } catch {
        if (!cancelled) setError(t('benchmark.noData'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [token, t]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4">
        <Spinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-2xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4 text-xs text-[var(--aif-text-muted)] text-center">
        {error || t('benchmark.noData')}
      </div>
    );
  }

  const {
    farm_db_per_ha,
    farm_percentile,
    best_crop,
    worst_crop,
    source,
  } = data;

  const cat = percentileCategory(farm_percentile);
  const cfg = CATEGORY_CONFIG[cat];

  return (
    <div className="rounded-2xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4 space-y-3">
      {/* Header */}
      <div className="text-xs font-semibold uppercase tracking-wide text-[var(--aif-text-muted)]">
        {t('benchmark.farmTitle')}
      </div>

      {/* Farm DB/ha + percentile */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-lg font-bold text-[var(--aif-text-primary)]">
            {Math.round(farm_db_per_ha)} €/ha
          </div>
          <div className="text-[10px] text-[var(--aif-text-muted)]">
            {t('benchmark.farmPercentile')}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold text-[var(--aif-text-primary)]">
            {Math.round(farm_percentile)}.
          </div>
          <div className={`text-[10px] font-semibold ${cfg.color}`}>
            {cfg.emoji} {t(cfg.key)}
          </div>
        </div>
      </div>

      {/* Best / Worst crop */}
      <div className="grid grid-cols-2 gap-2">
        {best_crop && (
          <div className="rounded-lg border border-green-500/20 bg-green-500/5 p-2 text-center">
            <div className="text-[10px] text-[var(--aif-text-muted)]">{t('benchmark.bestCrop')}</div>
            <div className="text-xs font-bold text-green-400 mt-0.5">{best_crop.crop_name || best_crop.name}</div>
            {best_crop.db_per_ha != null && (
              <div className="text-[10px] text-green-400">{Math.round(best_crop.db_per_ha)} €/ha</div>
            )}
          </div>
        )}
        {worst_crop && (
          <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-2 text-center">
            <div className="text-[10px] text-[var(--aif-text-muted)]">{t('benchmark.worstCrop')}</div>
            <div className="text-xs font-bold text-red-400 mt-0.5">{worst_crop.crop_name || worst_crop.name}</div>
            {worst_crop.db_per_ha != null && (
              <div className="text-[10px] text-red-400">{Math.round(worst_crop.db_per_ha)} €/ha</div>
            )}
          </div>
        )}
      </div>

      {/* "Better than X% of farms" */}
      <div className="text-xs text-center text-[var(--aif-text-secondary)]">
        {t('benchmark.betterThan', { pct: Math.round(farm_percentile) })}
      </div>

      {/* Source */}
      {source && (
        <div className="text-[10px] text-[var(--aif-text-muted)] text-center pt-1 border-t border-[var(--aif-border-default)]">
          {t('benchmark.source')}: {source}
        </div>
      )}
    </div>
  );
}
